import { DatePipe } from '@angular/common';
import { CountryCode } from 'app/core';

export enum DateTimeFormatterFormatType {
    Time,
    Date,
}

/**
 * Service to provide necessary information based on user's locale.
 * This service must be extended, all abstract methods implemented and provided at "root level"
 *
 * @export
 */
export abstract class LocaleService {
    /** Provides the formatted date for the current locale */
    abstract format(date: Date, format: DateTimeFormatterFormatType, extended?: boolean): string;

    /** Provides the token for the current locale */
    abstract getCurrentLocaleToken(): string;

    /** Provides the separator for separating start and end of week for the current locale */
    abstract getCurrentWeekDaySeparator(): string;

    /** Provides the alternative diacritics regex for the current locale */
    getAlternativeDiacriticsRegex(): RegExp {
        return /[AEIOUCNaeioucn]/gi;
    }

    /** Provides the date´s month name for the current locale */
    abstract getDateMonthNameFormat(): Intl.DateTimeFormat;

    /** Provides the marker's full date´s format for the current locale */
    getDateTimeMarkerFormat(): string {
        return "d LLLL - H'h'mm";
    }

    validDate(date: any): boolean {
        return date instanceof Date && !isNaN(date.getTime());
    }

    parseStringToDate(date: string, format: string, delimiter: string): Date
    {
        const formatLowerCase: string = format.toLowerCase();
        const formatItems: string[] = formatLowerCase.split(delimiter);
        const dateItems: string[] = date.split(delimiter);
        
        const monthIndex: number = formatItems.indexOf('mm');
        const dayIndex: number = formatItems.indexOf('dd');
        const yearIndex: number = formatItems.indexOf('yyyy');
        
        let month = parseInt(dateItems[monthIndex], 10);
        month -= 1;
        
        const formatedDate = new Date(
            parseInt(dateItems[yearIndex], 10), 
            month, 
            parseInt(dateItems[dayIndex], 10)
        );
        
        return formatedDate;
    }

    /** Tries to parse string as date using different formats */
    getStringAsDate(date: string): Date {
        let d = new Date(date);
                
        if (!this.validDate(d)) {
            d = this.parseStringToDate(date, 'dd/MM/yyyy', '/');

            if (!this.validDate(d)) {
                d = this.parseStringToDate(date, 'dd-MM-yyyy', '-');

                if (!this.validDate(d)) { 
                    this.parseStringToDate(date, 'mm/dd/yyyy', '/');
                
                    if (!this.validDate(d)) { 
                        d = this.parseStringToDate(date, 'mm-dd-yyyy', '-');
                    }
                }
            }
        }

        return d;
    }

    /** Provides the schedule's birth date´s format for the current locale */
    abstract getDateExtended(date: Date, showWeekDay?: boolean): string;

    /** Provides the date´s format for the current locale */
    abstract getDateTimeExtended(date: Date, showWeekDay?: boolean): string;

    /** Provides a short version of the date for the current locale */
    abstract getDateShort(date: Date): string;

    /** Provides the diacritics map for the current locale
     * http://pinyin.info/unicode/diacritics.html
     * handle also multichar into single char (ae -> a e)
     */
    getDiacriticsMap(): { [key: string]: string } {
        return {
            a: 'aáàãâå',
            e: 'eéèêëē',
            i: 'iíìî',
            o: 'oóòõô',
            u: 'uúùûŭ',
            c: 'cçč',
            n: 'nñ',
        };
    }

    /** Provides the elasped time format for the current locale */
    abstract getElapsedTimeSlugs(): string[];

    /** Provides the normalize term regex for the current locale */
    getNormalizedTerm(term: string, form: 'NFC' | 'NFD' | 'NFKC' | 'NFKD' = 'NFD'): string {
        return term
            .trim()
            .normalize(form)
            .replace(/[\u0300-\u036f]/g, '');
    }

    /** Provides the phrase connectors regex for the current locale */
    abstract getPhraseConnectorsRegex(): string;

    /** Provides the phone number regex for the current locale */
    abstract getPhoneNumberRegex(): string;

    /** Provides the phone number with dial code regex for the current locale */
    getPhoneNumberWithDialCodeRegex(dialCodePhoneNumberRegex: string, forceDialCode: boolean = false): string {
        const phoneRegex = this.getPhoneNumberRegex();
        let phoneWithDialCodeRegex = dialCodePhoneNumberRegex; 
        
        if (!forceDialCode) {
            phoneWithDialCodeRegex += '?'; 
        }

        phoneWithDialCodeRegex += phoneRegex.slice(1, phoneRegex?.length);
        return phoneWithDialCodeRegex;
    }

    /** Provides the dial code phone number regex for the current locale */
    getDialCodePhoneNumberRegex(): string {
        return '^(\\+[0-9\\-]{1,})';
    }

    /** Provides the email regex for the current locale */
    getEmailRegex(): string {
        return '/S+@S+.S+/';
    }

    /** Provides the fiscal number regex for the current locale */
    abstract getFiscalNumberRegex(): string;

    /** Provides the full date´s format for the current locale */
    getRangeDateFormat(isSameYear: boolean): string {
        return isSameYear ? 'd MMMM' : 'd MMMM yyyy';
    }

    /** Provides the range dates separator for the current locale */
    abstract getRangeDateSeparator(): string;

    /** Provides the sms code regex for the current locale */
    getSMSCodeRegex(): string {
        return '[0-9]{6}';
    }

    /** Provides the sanitize term regex for the current locale */
    getSanitizeTermRegex(): RegExp {
        return /[^A-Za-z0-9]/gi;
    }

    /** Validates the fiscal number for the current locale */
    abstract validateFiscalNumber(fiscalNumber: string): boolean;

    getSearchMatches(searchTerm: string, termToMatch: string, caseSensitive: boolean = false): number {
        let counter: number = 0;
        let matchFlag: string = '';

        // remove diacritics from current searchTerm
        let searchTermNormalized = this.getNormalizedTerm(searchTerm);
        searchTermNormalized = this.sanitizeTerm(searchTermNormalized);
        searchTermNormalized = this.addAlternativeDiacritics(searchTermNormalized);

        let content = termToMatch.trim();

        if (content && searchTermNormalized.length > 0) {
            if (!caseSensitive) {
                matchFlag = 'gi';
            } else {
                matchFlag = 'g';
            }

            content.replace(new RegExp(searchTermNormalized, matchFlag), (match) => {
                counter++;
                return '';
            });
        }

        return counter;
    }

    getDateSearchMatches(searchTerm: string, dateToMatch: Date): number {
        const formatsToMatch = ['dd-MM-yyyy', 'dd MM yyyy', 'dd/MM/yyyy'];
        const datePipe = new DatePipe(this.getCurrentLocaleToken());

        // Try to find matches for the most common date formats
        for (const format of formatsToMatch) {
            const formattedDate = datePipe.transform(dateToMatch, format);
            const matches = this.getSearchMatches(searchTerm, formattedDate);
            if (matches > 0) {
                return matches;
            }
        }

        const extendedDate = this.getDateExtended(dateToMatch);
        const extendedMatches = this.getSearchMatches(searchTerm, extendedDate);
        if (extendedMatches > 0) {
            return extendedMatches;
        }

        const extendedDateTime = this.format(dateToMatch, DateTimeFormatterFormatType.Time);
        const extendedTimeMatches = this.getSearchMatches(searchTerm, extendedDateTime);
        return extendedTimeMatches;
    }

    private addAlternativeDiacritics(searchTerm: string): string {
        if (searchTerm) {
            return searchTerm.replace(this.getAlternativeDiacriticsRegex(), (match) => {
                return `[${this.getLetterDiacriticsMap(match)}]`;
            });
        }

        return searchTerm;
    }

    private getLetterDiacriticsMap(letter: string): string {
        const diacriticsMap = this.getDiacriticsMap();

        const letterDiacritics: string = diacriticsMap[letter.toLowerCase()];
        return letter === letter.toLowerCase() ? letterDiacritics : letterDiacritics.toUpperCase();
    }

    private sanitizeTerm(searchTerm: string): string {
        if (searchTerm) {
            return searchTerm.replace(this.getSanitizeTermRegex(), (match) => {
                return `[${match}]`;
            });
        }

        return searchTerm;
    }
}
